import { FolderCopy } from "@mui/icons-material";
import Topic from "@mui/icons-material/Topic";
import Warehouse from "@mui/icons-material/Warehouse";
import { Chip, CircularProgress, LinearProgress, Tooltip, Typography } from "@mui/material";
import { clone, cloneDeep, forEach } from 'lodash';
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Api from "../../Api";
import { IContract, IContractVariation, IMaterial } from "../../interfaces";
import { Sort } from "../../utils";
import { BreadcrumbBar } from "../Breadcrumb";
import { ExpandableContainer } from "../Shared/ExpandableContainer";
import FormNavigation from "../Shared/FormNavigation";
import { ContractVariationActions } from "./ContractVariationActions";
import { ContractVariationDetailForm } from "./ContractVariationDetail";
import "./index.scss";
import { MaterialInformationForm } from "./MaterialInformation";
import { RemovedMaterialInformationForm } from "./RemovedMaterialInformation";


export const ContractVariation: React.FC = ({
    ...props
}) => {

    const [contract, setContract] = useState<IContract>();
    const [contractVariation, setContractVariation] = useState<IContractVariation>();

    const [linkedC, setLinkedC] = useState<{ contractId: string, reference: string } | null>(null);
    const [linkedSR, setLinkedSR] = useState<{ storageRequestId: string, reference: string } | null>(null);

    const [detailsValid, setDetailsValid] = useState<boolean>(false);
    const [materials, setMaterials] = useState<IMaterial[]>([]);
    const [deletedMaterials, setDeletedMaterials] = useState<IMaterial[]>([]);

    const [bread, setBread] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(true);

    const [unsavedChanges, setUnsavedChanges] = useState<boolean>(false);

    const { cvId, cId } = useParams();

    const navigate = useNavigate();

    useEffect(() => {

        if (cvId) setBread("Update");
        else {
            setBread("Create");
        }

        //when opening an existing request we will pass
        //in the id on the fetch request.

        fetchFormData(cvId, cId).then((data: any) => {
            populateForm(data);
        });

    }, []);// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        validateDetails();
    }, [materials, deletedMaterials, contractVariation])

    const fetchFormData = async (contractVariationId: string | null | undefined, contractId: string | null | undefined) => {

        if (contractVariationId && contractId) {

            return Api.Contracts.Variation.get(contractId, contractVariationId).then((cv) => {

                console.log("variation loaded", cv);

                return cv;
            });

        } else if (contractId) {


            return Api.Contracts.Variation.create(contractId).then((data) => {

                //update the url so page reloads dont cause
                //multiple requests to be created

                var cv = data as IContractVariation;
                window.history.pushState("Update", "Update Contract Variation", `/contractvariation/update/${contractId}/${cv.contractVariationId}`);

                return cv;
            });
        }
        else {
            console.error("contract or contract variation Id is missing")
            return {};
        };
    }

    const populateForm = async (data: any) => {

        if (data) {

            let cv = data as IContractVariation;
            if (cv.updatedContract === null)
                cv.updatedContract = cloneDeep(cv.contract);


            setContractVariation(cv);
        }

        if (data.contract) {
            setContract(data.contract);
            setLinkedC({
                contractId: data.contract.contractId,
                reference: data.contract.reference
            })


            Sort.materials(data.updatedContract.materials);
            Sort.materials(data.contract.materials);

            if (data.updatedContract)
                setMaterials(data.updatedContract.materials);
            else setMaterials(data.contract.materials ?? []);


            if (data.removedMaterials) {

                let materials: IMaterial[] = [];
                for (var i = 0; i < data.removedMaterials.length; i++) {

                    let remaining: IMaterial = data.updatedContract.materials.filter((x: IMaterial) => { return x.materialId === data.removedMaterials[i].materialId })[0];

                    if (remaining) {

                        //only some quantity was removed so take the original material and reflect the change

                        let removedMaterial = cloneDeep(remaining);
                        removedMaterial.quantity = data.removedMaterials[i].quantity;

                        materials.push(removedMaterial);


                    } else {

                        //material was fully deleted so we can get it to display
                        await Api.Materials.get(data.removedMaterials[i].materialId).then((material) => {
                            materials.push(material as IMaterial)
                        })
                    }
                }
                setDeletedMaterials(prev => {
                    return materials;
                });

            }
        }


        if (data.storageRequests?.some(() => { return true; })) {
            setLinkedSR({
                storageRequestId: data.storageRequests[0].storageRequestId,
                reference: data.storageRequests[0].storageRequestReference
            })
        }


        setLoading(false);
    }

    const onUpdateMaterials = (newMaterials: IMaterial[] | null | undefined) => {

        if (!newMaterials) return;


        let cv = cloneDeep(contractVariation) as IContractVariation;

        if (!cv.updatedContract)
            cv.updatedContract = cloneDeep(cv.contract) as IContract;

        cv.updatedContract.materials = newMaterials;

        setMaterials(newMaterials);
        setContractVariation(cv);

        onSave(cv);

    }

    const onImportComplete = (imported: IMaterial[]) => {
        let newMaterials = cloneDeep(materials);
        newMaterials = newMaterials.concat(imported);
        setMaterials(newMaterials);
    }

    const onConsumeMaterials = (materialIds: string[]) => {
        let cv = cloneDeep(contractVariation) as IContractVariation;
        cv.consumedMaterialIds = materialIds;
        setContractVariation(cv);
        onSave(cv);
    }

    const onRemoveMaterial = (material: IMaterial) => {

        if (material.materialId) {
            let cv = cloneDeep(contractVariation) as IContractVariation;


            //add the material to the removed materials list 
            if (!cv.removedMaterials)
                cv.removedMaterials = [];

            cv.removedMaterials.push({
                materialId: material.materialId,
                quantity: material.quantity ?? 0
            })

            //remove the material from the updated contract materials if entire quantity was removed
            let existing = cv.updatedContract.materials.filter(x => x.materialId === material.materialId)[0];
            if (existing.quantity === material.quantity)
                cv.updatedContract.materials = cv.updatedContract.materials
                    .filter(x => x.materialId !== material.materialId);

            setContractVariation(cv);

            //update deleted materials state
            if ((material.statusId ?? 0) > 1) {
                let deletedModel = cloneDeep(deletedMaterials);
                deletedModel.push(material)
                setDeletedMaterials(deletedModel);
            }

            //update materials state
            setMaterials(cv.updatedContract.materials);

            onSave(cv);
        }
    }

    const onReinstateMaterial = (material: IMaterial) => {

        if (material.materialId) {
            let cv = cloneDeep(contractVariation) as IContractVariation;

            cv.removedMaterials = cv.removedMaterials?.filter(x => x.materialId !== material.materialId) ?? [];

            if (!cv.updatedContract.materials.some(x => x.materialId === material.materialId)) {
                //this material was entirely removed so add it back in
                cv.updatedContract.materials.push(material);
            }


            Sort.materials(cv.updatedContract.materials);


            setContractVariation(cv);


            let deletedModel = cloneDeep(deletedMaterials);
            deletedModel = deletedModel.filter(x => x.materialId !== material.materialId);
            setDeletedMaterials(deletedModel);


            //update materials state
            setMaterials(cv.updatedContract.materials);

            onSave(cv);

        }
    }

    const onUpdateVariationDetail = (cv: IContractVariation) => {

        let reset = cv.isContractEnd !== contractVariation?.isContractEnd;
        if (reset) {
            cv.consumedMaterialIds = [];
            cv.removedMaterials?.forEach(rm => {
                if (!cv.updatedContract.materials.some(x => x.materialId === rm.materialId))
                    cv.updatedContract.materials.push(cv.contract.materials.filter(x => x.materialId === rm.materialId)[0])
            });
            cv.removedMaterials = [];

        }

        validateDetails();
        setContractVariation(cv);
        onSave(cv);
    }

    const onSave = (cv: IContractVariation) => {
        setUnsavedChanges(true);
        Api.Contracts.Variation.save(cv).then((r) => {
            populateForm(r);
            setUnsavedChanges(false);
        });
    }

    const validateDetails = () => {

        let form: HTMLFormElement = document.getElementById("contract-variation-details-form") as HTMLFormElement;
        if (form) {
            setDetailsValid(form.checkValidity());
        }

    }

    const onEnd = async (consumeMaterialsReason: string, createNewContract: boolean) => {

        if (contractVariation) {
            setLoading(true);
            return Api.Contracts.Variation.end(contractVariation, consumeMaterialsReason, createNewContract).then(async (r: any) => {

                fetchFormData(contractVariation?.contractVariationId, contract?.contractId).then((data: any) => {
                    populateForm(data);
                    if (r.contract)
                        setLinkedC({
                            contractId: r.contract.contractId,
                            reference: r.contract.reference ?? "NO-REF"
                        });
                    setLoading(false);
                    navigate("/contract");
                });
                setLoading(false);
            });
        }
    }

    const onSubmit = async () => {
        setLoading(true);
        return Api.Contracts.Variation.submit(contract?.contractId as string, contractVariation?.contractVariationId as string).then(async (r: any) => {

            fetchFormData(contractVariation?.contractVariationId, contract?.contractId).then((data: any) => {
                populateForm(data);

                if (r.contract)
                    setLinkedC({
                        contractId: r.contract.contractId,
                        reference: r.contract.reference ?? "NO-REF"
                    });


                setLoading(false);
                navigate("/contract");
            });

        });
    }
    const onDelete = async () => {
        setLoading(true);
        return Api.Contracts.Variation.delete(contract?.contractId as string, contractVariation?.contractVariationId as string).then(async (r: any) => {
            navigate("/contract");

        });
    }
    const onApprove = async () => {
        setLoading(true);
        return Api.Contracts.Variation.approve(contract?.contractId as string, contractVariation?.contractVariationId as string).then(async (r) => {

            fetchFormData(contractVariation?.contractVariationId, contract?.contractId).then((data: any) => {
                populateForm(data);
                setLoading(false);

            });

        });
    }

    const onComplete = async () => {
        setLoading(true);
        return Api.StorageRequests.complete(contract?.contractId as string).then(async (r) => {

            fetchFormData(contractVariation?.contractVariationId, contract?.contractId).then((data: any) => {
                populateForm(data);
                setLoading(false);
            });

        });
    }

    const onReject = async (reason: string) => {
        setLoading(true);

        return Api.Contracts.Variation.reject(contract?.contractId as string, contractVariation?.contractVariationId as string, reason).then(async (r) => {

            fetchFormData(contractVariation?.contractVariationId, contract?.contractId).then((data: any) => {
                populateForm(data);
                setLoading(false);

            });

        });
    }

    const onClose = async () => {
        setLoading(true);

        return Api.Contracts.Variation.close(contract?.contractId as string, contractVariation?.contractVariationId as string).then(async (r) => {

            fetchFormData(contractVariation?.contractVariationId, contract?.contractId).then((data: any) => {
                populateForm(data);
                setLoading(false);

            });

        });
    }

    const getStatus = () => { return contractVariation?.status ?? 0 }

    const getStatusLabel = () => {

        if (loading)
            return <CircularProgress size="2rem" />;

        switch (getStatus()) {
            case 1:
                return <Chip
                    label={"Draft Variation"}
                    color="default"
                    variant="filled"
                />;
            case 2:
                return <Chip
                    label={"Awaiting Approval - Open"}
                    color="primary"
                    variant="outlined"
                />;
            case 3: return <Chip
                label={"CV Created - Closed"}
                color="primary"
                variant="outlined"
            />;
            case 4: return <Chip
                label={"Rejected - Open"}
                color="primary"
                variant="outlined"
            />;
            case 5: return <Chip
                label={"Rejected - Closed"}
                color="default"
                variant="filled"
            />;
            case 6: return <Chip
                label={"Approved - Closed"}
                color="default"
                variant="filled"
            />;
            case 7: return <Chip
                label={"Ended"}
                color="default"
                variant="filled"
            />;
            default:
                return <Chip
                    label={"..."}
                    style={{ minWidth: '4rem' }}
                    color="primary"
                    variant="outlined"
                />;
        }
    }

    const getLinkLabel = () => {

        let contractLink = linkedC ? <Tooltip title="Go to contract / lease list" placement="right" >
            <Chip
                sx={{ ml: 2, pl: 0.5, cursor: 'pointer' }}
                icon={<Topic />}
                label={linkedC.reference}
                color="primary"
                variant="filled"
                onClick={() => navigate('/contract/update/' + linkedC.contractId)}
            />
        </Tooltip> : <></>;

        let storageRequestLink = linkedSR ?
            <Tooltip title="Go to storage request" placement="right" >
                <Chip
                    sx={{ ml: 2, pl: 0.5, cursor: 'pointer' }}
                    icon={<Warehouse />}
                    label={linkedSR.reference}
                    color="primary"
                    variant="filled"
                    onClick={() => navigate('/storagerequest/update/' + linkedSR.storageRequestId)}

                />
            </Tooltip> : <></>;

        return <>{contractLink}{storageRequestLink}</>;
    }

    const isReadonly = () => {
        return ![1, 4].includes(getStatus());
    }

    return (
        <ExpandableContainer>
            <BreadcrumbBar
                current={bread}
                previous={[{
                    name: "Contract / Lease List",
                    href: "/contract"
                },
                {
                    name: "Variation",
                    href: "/contract"
                },
                ]}
            />
            <Typography variant="h4" sx={{ py: 3 }}>{contract ? <>
                <FolderCopy fontSize="inherit" sx={{ opacity: .8 }} color="primary" />
                {contractVariation?.cvNumber ? contractVariation.cvNumber : contract.reference}
                <FormNavigation />
            </> : <CircularProgress />} </Typography>

            {getStatusLabel()}
            {getLinkLabel()}

            {contractVariation && <>

                <form id="contract-variation-details-form">
                    <div className="form-section">
                        <ContractVariationDetailForm
                            contractVariation={contractVariation}
                            updateVariation={onUpdateVariationDetail}
                            readonly={isReadonly()}
                        />
                    </div>
                </form>


                <div className="form-section">
                    <MaterialInformationForm
                        status={getStatus()}
                        materials={materials}
                        deleted={deletedMaterials}
                        onUpdateMaterials={onUpdateMaterials}
                        onConsumeMaterials={onConsumeMaterials}
                        onRemoveMaterial={onRemoveMaterial}
                        onImportComplete={onImportComplete}
                        readonly={isReadonly()}
                        isContractEnd={contractVariation.isContractEnd ?? false}
                        saving={unsavedChanges}
                    />
                </div>


                {contractVariation?.isContractEnd !== true && deletedMaterials.length > 0 &&
                    <div className="form-section">
                        <RemovedMaterialInformationForm
                            status={getStatus()}
                            materials={deletedMaterials}
                            consumedMaterials={contractVariation?.consumedMaterialIds}
                            onUpdateMaterials={onUpdateMaterials}
                            onConsumeMaterials={onConsumeMaterials}
                            onReinstateMaterial={onReinstateMaterial}
                            readonly={isReadonly()}
                            saving={unsavedChanges}
                        />
                    </div>
                }


                <div className="form-section">
                    {loading ? <LinearProgress /> :
                        <ContractVariationActions
                            materials={materials}
                            consumed={contractVariation?.consumedMaterialIds ?? []}
                            status={getStatus()}
                            formValid={detailsValid}
                            contractEnd={contractVariation?.isContractEnd ?? false}
                            {...{
                                onEnd,
                                onSubmit,
                                onDelete,
                                onApprove,
                                onReject,
                                onClose,
                                onComplete
                            }}
                            readonly={false}
                        />
                    }
                </div>



            </>}
        </ExpandableContainer>
    );
}